class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// class PriceRange extends HTMLElement {
//   constructor() {
//     super();
//     this.querySelectorAll('input').forEach((element) => {
//       element.addEventListener('change', this.onRangeChange.bind(this));
//       element.addEventListener('keydown', this.onKeyDown.bind(this));
//     });
//     this.setMinAndMaxValues();
//   }

//   onRangeChange(event) {
//     this.adjustToValidValues(event.currentTarget);
//     this.setMinAndMaxValues();
//   }

//   onKeyDown(event) {
//     if (event.metaKey) return;

//     const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
//     if (!event.key.match(pattern)) event.preventDefault();
//   }

//   setMinAndMaxValues() {
//     const inputs = this.querySelectorAll('input');
//     const minInput = inputs[0];
//     const maxInput = inputs[1];
//     if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
//     if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
//     if (minInput.value === '') maxInput.setAttribute('data-min', 0);
//     if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
//   }

//   adjustToValidValues(input) {
//     const value = Number(input.value);
//     const min = Number(input.getAttribute('data-min'));
//     const max = Number(input.getAttribute('data-max'));

//     if (value < min) input.value = min;
//     if (value > max) input.value = max;
//   }
// }

// customElements.define('price-range', PriceRange);
// class PriceRange extends HTMLElement {
//   constructor() {
//     super();

//     this.minSlider  = this.querySelector('.range-min');
//     this.maxSlider  = this.querySelector('.range-max');
//     this.fillEl     = this.querySelector('.price-range__fill');
//     this.minDisplay = this.querySelector('.price-range__min-display');
//     this.maxDisplay = this.querySelector('.price-range__max-display');
//     this.hiddenMin  = this.querySelector('.price-input-min');
//     this.hiddenMax  = this.querySelector('.price-input-max');

//     this.maxValue = parseFloat(this.dataset.max) || parseFloat(this.maxSlider.max);
//     this.minValue = parseFloat(this.dataset.min) || 0;

//     this._debounceTimer = null;

//     // Initially disabled - jab tak slider move na ho
//     if (this.hiddenMin) this.hiddenMin.disabled = true;
//     if (this.hiddenMax) this.hiddenMax.disabled = true;

//     this._init();
//     this._updateFill();
//   }

//   _init() {
//     this.minSlider.addEventListener('input', () => this._onMinSliderChange());
//     this.maxSlider.addEventListener('input', () => this._onMaxSliderChange());
//   }

//   _onMinSliderChange() {
//     let min = parseFloat(this.minSlider.value);
//     let max = parseFloat(this.maxSlider.value);

//     if (min >= max) {
//       min = max - 1;
//       this.minSlider.value = min;
//     }

//     this._syncHidden(min, null);
//     this._updateDisplay(min, null);
//     this._updateFill();
//     this._debounceFilter();
//   }

//   _onMaxSliderChange() {
//     let min = parseFloat(this.minSlider.value);
//     let max = parseFloat(this.maxSlider.value);

//     if (max <= min) {
//       max = min + 1;
//       this.maxSlider.value = max;
//     }

//     this._syncHidden(null, max);
//     this._updateDisplay(null, max);
//     this._updateFill();
//     this._debounceFilter();
//   }

//   _syncHidden(min, max) {
//     if (min !== null && this.hiddenMin) this.hiddenMin.value = min;
//     if (max !== null && this.hiddenMax) this.hiddenMax.value = max;
//   }

//   _updateDisplay(min, max) {
//     if (min !== null && this.minDisplay) this.minDisplay.textContent = min;
//     if (max !== null && this.maxDisplay) this.maxDisplay.textContent = max;
//   }

//   _updateFill() {
//     if (!this.fillEl) return;
//     const min   = parseFloat(this.minSlider.value);
//     const max   = parseFloat(this.maxSlider.value);
//     const total = this.maxValue - this.minValue;

//     const leftPct  = ((min - this.minValue) / total) * 100;
//     const rightPct = ((this.maxValue - max) / total) * 100;

//     this.fillEl.style.left  = `${leftPct}%`;
//     this.fillEl.style.right = `${rightPct}%`;
//   }

//   _debounceFilter() {
//     clearTimeout(this._debounceTimer);
//     this._debounceTimer = setTimeout(() => this._triggerFilter(), 500);
//   }

//   _triggerFilter() {
//     const form = this.closest('form');
//     if (!form) return;

//     const min = parseFloat(this.minSlider.value);
//     const max = parseFloat(this.maxSlider.value);

//     // Default values hain to disabled rakho - price filter URLs mein nahi jayega
//     if (min === this.minValue && max === this.maxValue) {
//       if (this.hiddenMin) this.hiddenMin.disabled = true;
//       if (this.hiddenMax) this.hiddenMax.disabled = true;
//     } else {
//       // Custom values hain to enable karo
//       if (this.hiddenMin) this.hiddenMin.disabled = false;
//       if (this.hiddenMax) this.hiddenMax.disabled = false;
//     }

//     form.dispatchEvent(new Event('input', { bubbles: true }));
//   }
// }

// customElements.define('price-range', PriceRange);

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minSlider  = this.querySelector('.range-min');
    this.maxSlider  = this.querySelector('.range-max');
    this.fillEl     = this.querySelector('.price-range__fill');
    this.minDisplay = this.querySelector('.price-range__min-display');
    this.maxDisplay = this.querySelector('.price-range__max-display');
    this.hiddenMin  = this.querySelector('.price-input-min');
    this.hiddenMax  = this.querySelector('.price-input-max');

    this.maxValue = this._parse(this.dataset.max);
    this.minValue = this._parse(this.dataset.min) || 0;

    this._debounceTimer = null;
    this._initialized   = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const urlParams = new URLSearchParams(window.location.search);
    const urlMinRaw = urlParams.get('filter.v.price.gte');
    const urlMaxRaw = urlParams.get('filter.v.price.lte');

    const urlMinVal = urlMinRaw !== null ? Math.round(this._parse(urlMinRaw)) : null;
    const urlMaxVal = urlMaxRaw !== null ? Math.round(this._parse(urlMaxRaw)) : null;

    const hasActivePriceFilter =
      (urlMinVal !== null && urlMinVal !== this.minValue) ||
      (urlMaxVal !== null && urlMaxVal !== this.maxValue);

    if (this.hiddenMin) this.hiddenMin.disabled = !hasActivePriceFilter;
    if (this.hiddenMax) this.hiddenMax.disabled = !hasActivePriceFilter;

    if (urlMinVal !== null) {
      this.minSlider.value = urlMinVal;
      if (this.minDisplay) this.minDisplay.textContent = urlMinVal;
    }
    if (urlMaxVal !== null) {
      this.maxSlider.value = urlMaxVal;
      if (this.maxDisplay) this.maxDisplay.textContent = urlMaxVal;
    }

this._init();

// ✅ FULL SYNC FIX
const min = this._parse(this.minSlider.value);
const max = this._parse(this.maxSlider.value);

this._syncHidden(min, max);
this._updateDisplay(min, max);
this._updateFill();
this._updateZIndex();
  }

  _parse(value) {
    if (value === null || value === undefined || value === '') return 0;
    return parseFloat(String(value).replace(/,/g, '').trim()) || 0;
  }

  _init() {
    this.minSlider.addEventListener('input', () => this._onMinSliderChange());
    this.maxSlider.addEventListener('input', () => this._onMaxSliderChange());
  }

  // Min slider right end pe ho to uska z-index badhao
  _updateZIndex() {
    const min    = this._parse(this.minSlider.value);
    const total  = this.maxValue - this.minValue;
    const minPct = total > 0 ? ((min - this.minValue) / total) * 100 : 0;

    this.minSlider.style.zIndex = minPct >= 80 ? 4 : 2;
    this.maxSlider.style.zIndex = 1;
  }

  _onMinSliderChange() {
    let min = this._parse(this.minSlider.value);
    let max = this._parse(this.maxSlider.value);

    if (min >= max) {
  const GAP = this.maxValue > 1000 ? 50 : 1;
min = Math.max(this.minValue, max - GAP);
      this.minSlider.value = min;
    }

    this._syncHidden(min, null);
    this._updateDisplay(min, null);
    this._updateFill();
    this._updateZIndex();
    this._debounceFilter();
  }

  _onMaxSliderChange() {
    let min = this._parse(this.minSlider.value);
    let max = this._parse(this.maxSlider.value);

    if (max <= min) {
      max = Math.min(this.maxValue, min + 1);
      this.maxSlider.value = max;
    }

    this._syncHidden(null, max);
    this._updateDisplay(null, max);
    this._updateFill();
    this._updateZIndex();
    this._debounceFilter();
  }

  _syncHidden(min, max) {
    if (min !== null && this.hiddenMin) this.hiddenMin.value = Math.round(min);
    if (max !== null && this.hiddenMax) this.hiddenMax.value = Math.round(max);
  }

  _updateDisplay(min, max) {
    if (min !== null && this.minDisplay) this.minDisplay.textContent = min;
    if (max !== null && this.maxDisplay) this.maxDisplay.textContent = max;
  }

_updateFill() {
  if (!this.fillEl) return;

  const min   = this._parse(this.minSlider.value);
  const max   = this._parse(this.maxSlider.value);
  const total = this.maxValue - this.minValue;

  if (total <= 0) return;

  let leftPct  = ((min - this.minValue) / total) * 100;
  let rightPct = ((this.maxValue - max) / total) * 100;

  // ✅ clamp values (important)
  leftPct  = Math.min(Math.max(leftPct, 0), 100);
  rightPct = Math.min(Math.max(rightPct, 0), 100);

  // ❌ remove this buggy condition
  // if (leftPct + rightPct >= 100) return;

  this.fillEl.style.left  = `${leftPct}%`;
  this.fillEl.style.right = `${rightPct}%`;
}

  _debounceFilter() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._triggerFilter(), 500);
  }

  _triggerFilter() {
    const form = this.closest('form');
    if (!form) return;

    const min = this._parse(this.minSlider.value);
    const max = this._parse(this.maxSlider.value);

    const isDefault = min === this.minValue && max === this.maxValue;
    if (this.hiddenMin) this.hiddenMin.disabled = isDefault;
    if (this.hiddenMax) this.hiddenMax.disabled = isDefault;

    form.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
